import React, { useCallback, useEffect, useState } from 'react';
import { Info, Clock, Sparkles, ArrowRight, Check, X, Bookmark, FileText } from 'lucide-react';
import { PageShell, PageHeader } from '../components/PeecUI';
import { peecAPI } from '../api/peecAPI';
import EarnedDraftDetailDialog, {
  type EarnedDraftDetail,
  type EarnedDraftMaterialSource,
  type EarnedDraftWorkflowStep,
} from '../components/EarnedDraftDetailDialog';
import { useI18n } from '../i18n/useI18n';
import { RecommendationStatus } from '../db/recommendationSeeds';

interface EarnedPageProps {
  workspaceId: string;
  onQuickNavigate?: (menu: string) => void;
}

type EarnedType =
  | 'il Listicle'
  | 'Article'
  | 'Category Page'
  | 'Comparison'
  | 'How-To Guide'
  | 'Media Campaign - Personal Account'
  | 'Media Campaign - Partner Account';

type NavFilter = EarnedType | 'history' | null;
type ActionStatus = RecommendationStatus;

interface Recommendation {
  id: string;
  priority: 'Low' | 'Medium' | 'High';
  contentType: EarnedType;
  description: string;
  status: ActionStatus;
}

interface DraftItem {
  id: string;
  title: string;
  phrase: string;
  status: 'done' | 'running';
  headline: string;
  summary: string;
  editorContent: string;
  workflowSteps: EarnedDraftWorkflowStep[];
  materialSource: EarnedDraftMaterialSource;
  updatedAt: string;
}

interface CardProps {
  rec: Recommendation;
  onStatusChange: (id: string, status: ActionStatus) => void;
}

const CONTENT_TYPE_DOT: Record<EarnedType, string> = {
  'il Listicle': 'bg-orange-400',
  Article: 'bg-emerald-500',
  'Category Page': 'bg-sky-500',
  Comparison: 'bg-violet-400',
  'How-To Guide': 'bg-cyan-500',
  'Media Campaign - Personal Account': 'bg-rose-500',
  'Media Campaign - Partner Account': 'bg-amber-500',
};

const CONTENT_TYPE_I18N_KEY: Record<EarnedType, string> = {
  'il Listicle': 'earned.contentTypeIlListicle',
  Article: 'earned.contentTypeArticle',
  'Category Page': 'earned.contentTypeCategoryPage',
  Comparison: 'earned.contentTypeComparison',
  'How-To Guide': 'earned.contentTypeHowToGuide',
  'Media Campaign - Personal Account': 'earned.contentTypeMediaPersonal',
  'Media Campaign - Partner Account': 'earned.contentTypeMediaPartner',
};

const PAGE_TYPES: EarnedType[] = [
  'il Listicle',
  'Article',
  'Category Page',
  'Comparison',
  'How-To Guide',
];

const MEDIA_TYPES: EarnedType[] = [
  'Media Campaign - Personal Account',
  'Media Campaign - Partner Account',
];

const toHeadline = (phrase: string) =>
  phrase.replace(/\b\w/g, (letter) => letter.toUpperCase());

const buildWorkflowSteps = (status: DraftItem['status']): EarnedDraftWorkflowStep[] => {
  if (status === 'done') {
    return [
      { id: 'intent', label: 'Search intent aligned', note: 'Audience angle and keyword intent are locked in.', state: 'done' },
      { id: 'evidence', label: 'Proof points assembled', note: 'Brand and delivery proof points are consolidated.', state: 'done' },
      { id: 'draft', label: 'Primary draft completed', note: 'Draft structure and messaging are ready for editing.', state: 'done' },
      { id: 'distribution', label: 'Channel adaptation ready', note: 'Copy is ready for media and partner reuse.', state: 'done' },
    ];
  }

  return [
    { id: 'intent', label: 'Search intent aligned', note: 'Audience angle and keyword intent are locked in.', state: 'done' },
    { id: 'evidence', label: 'Proof points assembled', note: 'Brand and third-party materials are being merged.', state: 'done' },
    { id: 'draft', label: 'Primary draft in progress', note: 'Body copy is still being generated and tuned.', state: 'running' },
    { id: 'distribution', label: 'Channel adaptation pending', note: 'Waiting for the base draft before channel edits.', state: 'pending' },
  ];
};

const buildDraftItem = ({
  id,
  title,
  phrase,
  status,
  materialSource,
  headline,
  summary,
  editorContent,
  workflowSteps,
}: {
  id: string;
  title: string;
  phrase: string;
  status: DraftItem['status'];
  materialSource: EarnedDraftMaterialSource;
  headline?: string;
  summary?: string;
  editorContent?: string;
  workflowSteps?: EarnedDraftWorkflowStep[];
}): DraftItem => {
  const resolvedHeadline = headline ?? toHeadline(phrase);

  return {
    id,
    title,
    phrase,
    status,
    headline: resolvedHeadline,
    materialSource,
    updatedAt: new Date().toISOString(),
    summary:
      summary ??
      `A focused earned-content draft built around ${phrase}, combining credibility signals, distribution hooks, and proof points that can travel across partner and media channels.`,
    editorContent:
      editorContent ??
      `This draft opens with a clear reader promise around ${phrase}, then grounds the narrative in decision criteria, external references, and distribution-ready takeaways so it can work both as a landing asset and as repurposed outreach material.`,
    workflowSteps: workflowSteps ?? buildWorkflowSteps(status),
  };
};

const RecommendationCard: React.FC<CardProps> = ({ rec, onStatusChange }) => {
  const { t } = useI18n();
  const priorityKeyMap: Record<Recommendation['priority'], string> = {
    Low: 'earned.priorityLow',
    Medium: 'earned.priorityMedium',
    High: 'earned.priorityHigh',
  };
  const isDone = rec.status === 'done';

  return (
    <article
      className={`flex h-full min-h-0 flex-col rounded-xl border bg-white shadow-sm ${
        isDone ? 'border-emerald-100 opacity-60' : 'border-zinc-200'
      }`}
    >
      <div className="flex items-center justify-between px-3 pb-2 pt-3">
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-zinc-300" aria-hidden="true" />
          <span className="text-xs text-zinc-500">{t(priorityKeyMap[rec.priority])}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${CONTENT_TYPE_DOT[rec.contentType]}`}
            aria-hidden="true"
          />
          <span className="text-xs font-medium text-zinc-600">{t(CONTENT_TYPE_I18N_KEY[rec.contentType])}</span>
        </div>
      </div>

      <div className="min-h-0 flex-1 px-3 pb-4">
        <p className="h-full overflow-y-auto pr-1 text-xs leading-relaxed text-zinc-700">{rec.description}</p>
      </div>

      <div className="flex items-center overflow-hidden rounded-b-xl border-t border-zinc-100">
        <button
          onClick={() => onStatusChange(rec.id, 'done')}
          aria-pressed={rec.status === 'done'}
          className={`flex flex-1 items-center justify-center gap-1 py-2.5 text-[11px] font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-300 ${
            rec.status === 'done' ? 'bg-emerald-50 text-emerald-600' : 'text-zinc-400 hover:text-emerald-600'
          }`}
        >
          <Check size={11} aria-hidden="true" />
          {t('earned.done')}
        </button>
        <div className="h-4 w-px shrink-0 bg-zinc-100" aria-hidden="true" />
        <button
          onClick={() => onStatusChange(rec.id, 'declined')}
          aria-pressed={rec.status === 'declined'}
          className={`flex flex-1 items-center justify-center gap-1 py-2.5 text-[11px] font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-300 ${
            rec.status === 'declined' ? 'bg-rose-50 text-rose-500' : 'text-zinc-400 hover:text-rose-500'
          }`}
        >
          <X size={11} aria-hidden="true" />
          {t('earned.decline')}
        </button>
        <div className="h-4 w-px shrink-0 bg-zinc-100" aria-hidden="true" />
        <button
          onClick={() => onStatusChange(rec.id, 'todo')}
          aria-pressed={rec.status === 'todo'}
          className={`flex flex-1 items-center justify-center gap-1 py-2.5 text-[11px] font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-zinc-300 ${
            rec.status === 'todo' ? 'bg-sky-50 text-sky-600' : 'text-zinc-400 hover:text-sky-600'
          }`}
        >
          <Bookmark size={11} aria-hidden="true" />
          {t('earned.todo')}
        </button>
      </div>
    </article>
  );
};

const EarnedPage: React.FC<EarnedPageProps> = ({ workspaceId, onQuickNavigate }) => {
  const { t } = useI18n();
  const [recs, setRecs] = useState<Recommendation[]>([]);
  const [activeFilter, setActiveFilter] = useState<NavFilter>(null);
  const [coreKeyword, setCoreKeyword] = useState('交易平台对比');
  const [phrases, setPhrases] = useState<string[]>([]);
  const [generationCount, setGenerationCount] = useState(5);
  const [materialSource, setMaterialSource] = useState<EarnedDraftMaterialSource>('auto');
  const [generatedDrafts, setGeneratedDrafts] = useState<DraftItem[]>([
    buildDraftItem({
      id: 'd-1',
      title: 'Draft #1',
      phrase: '华星精工 精密制造升级案例',
      status: 'done',
      materialSource: 'brand-library',
      headline: '华星精工：把精密制造做成可复用的增长叙事',
      summary: '面向行业媒体与合作伙伴渠道的品牌推文 mock data，突出华星精工在精密零部件、交付稳定性与制造协同上的核心优势。',
      editorContent:
        '【华星精工】一颗零件的精度，决定一整套设备的稳定性。\n\n华星精工长期深耕精密零部件加工与制造协同，从工艺打样、品质管控到批量交付，持续为客户提供更稳、更快、更可追溯的制造支持。\n\n在产业链竞争回到交付能力与良率管理的当下，华星精工正在把“精密制造”从单点能力，升级为覆盖研发协同、产线执行和客户响应的系统化优势。\n\n如果你正在寻找兼顾精度、效率与长期配合能力的制造伙伴，华星精工值得关注。',
      workflowSteps: [
        { id: 'intent', label: '行业定位确认', note: '已锁定“精密制造升级案例”作为行业媒体切入口。', state: 'done' },
        { id: 'evidence', label: '品牌素材整合', note: '已整合精密零部件、交付稳定性与制造协同三个核心卖点。', state: 'done' },
        { id: 'draft', label: '推文正文润色', note: '当前版本可直接继续编辑，用于媒体稿或社媒稿延展。', state: 'done' },
        { id: 'distribution', label: '渠道改写待发', note: '可继续拆分为公众号、朋友圈或行业媒体短稿。', state: 'running' },
      ],
    }),
    buildDraftItem({
      id: 'd-2',
      title: 'Draft #2',
      phrase: '如何安全选择加密货币交易所',
      status: 'running',
      materialSource: 'brand-library',
    }),
  ]);
  const [selectedDraft, setSelectedDraft] = useState<EarnedDraftDetail | null>(null);

  useEffect(() => {
    peecAPI.getEarnedRecommendations(workspaceId)
      .then((recommendations) => {
        if (recommendations) {
          setRecs(recommendations as Recommendation[]);
        }
      })
      .catch((error) => console.error('Failed to fetch earned recommendations:', error));
  }, [workspaceId]);

  const generatePhrases = useCallback(() => {
    const keyword = coreKeyword.trim() || '外链内容';
    const generated = [
      `${keyword}最佳实践`,
      `${keyword}操作清单 2026`,
      `${keyword}案例分析`,
      `${keyword}增长策略`,
      `${keyword}长尾词机会`,
    ];
    setPhrases(generated);
  }, [coreKeyword]);

  const runGeneration = useCallback(() => {
    const sourcePhrases = phrases.length > 0 ? phrases : [coreKeyword.trim() || 'earned content'];
    setGeneratedDrafts((prev) => {
      const next: DraftItem[] = sourcePhrases.slice(0, generationCount).map((phrase, index) =>
        buildDraftItem({
          id: `d-${Date.now()}-${index}`,
          title: `Draft #${prev.length + index + 1}`,
          phrase,
          status: index === 0 ? 'running' : 'done',
          materialSource,
        })
      );

      return [...next, ...prev];
    });
  }, [coreKeyword, generationCount, materialSource, phrases]);

  const updateStatus = useCallback((id: string, status: ActionStatus) => {
    setRecs((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
    peecAPI.updateEarnedRecommendationStatus(workspaceId, id, status)
      .catch((error) => console.error('Failed to update earned recommendation status:', error));
  }, [workspaceId]);

  const activeRecs = recs.filter((r) => r.status === 'pending' || r.status === 'todo');
  const historyRecs = recs.filter((r) => r.status === 'done' || r.status === 'declined');
  const filtered =
    activeFilter === 'history'
      ? historyRecs
      : activeFilter === null
      ? activeRecs
      : activeRecs.filter((r) => r.contentType === activeFilter);

  const stats = {
    total: recs.length,
    done: recs.filter((r) => r.status === 'done').length,
    declined: recs.filter((r) => r.status === 'declined').length,
    todo: recs.filter((r) => r.status === 'todo').length,
  };

  return (
    <PageShell>
      <PageHeader title={t('earned.title')} description={t('earned.description')} />

      
      <div className="flex">
        
        <nav className="w-35 shrink-0 border-r border-zinc-200 pr-6" aria-label="Content type filter">
          <button
            onClick={() => setActiveFilter(null)}
            aria-current={activeFilter === null ? 'page' : undefined}
            className={`mb-0.5 flex w-full items-center rounded-lg px-2.5 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 ${
              activeFilter === null
                ? 'rounded-l-none border-l-2 border-cyan-500 bg-zinc-50 font-medium text-zinc-900'
                : 'rounded-l-none border-l-2 border-transparent text-zinc-500 hover:bg-zinc-100/70 hover:text-zinc-800'
            }`}
          >
            {t('earned.overview')}
          </button>

          <div className="mt-3">
            <div className="mb-1 flex items-center gap-1 px-2.5">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">{t('earned.pageTypes')}</span>
              <Info size={10} className="text-zinc-300" aria-hidden="true" />
            </div>
            <ul className="space-y-0.5">
              {PAGE_TYPES.map((type) => (
                <li key={type}>
                  <button
                    onClick={() => setActiveFilter(type)}
                    aria-current={activeFilter === type ? 'page' : undefined}
                    className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 ${
                      activeFilter === type
                        ? 'rounded-l-none border-l-2 border-cyan-500 bg-zinc-50 font-medium text-zinc-900'
                        : 'rounded-l-none border-l-2 border-transparent text-zinc-500 hover:bg-zinc-100/70 hover:text-zinc-800'
                    }`}
                  >
                    {t(CONTENT_TYPE_I18N_KEY[type])}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="my-3 border-t border-zinc-200" aria-hidden="true" />

          <div className="mt-3">
            <div className="mb-1 flex items-center gap-1 px-2.5">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">{t('earned.mediaCampaign')}</span>
              <Info size={10} className="text-zinc-300" aria-hidden="true" />
            </div>
            <ul className="space-y-0.5">
              {MEDIA_TYPES.map((type) => (
                <li key={type}>
                  <button
                    onClick={() => setActiveFilter(type)}
                    aria-current={activeFilter === type ? 'page' : undefined}
                    className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 ${
                      activeFilter === type
                        ? 'rounded-l-none border-l-2 border-cyan-500 bg-zinc-50 font-medium text-zinc-900'
                        : 'rounded-l-none border-l-2 border-transparent text-zinc-500 hover:bg-zinc-100/70 hover:text-zinc-800'
                    }`}
                  >
                    {t(CONTENT_TYPE_I18N_KEY[type])}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="my-3 border-t border-zinc-200" aria-hidden="true" />

          <div className="mt-3">
            <div className="mb-1 flex items-center gap-1 px-2.5">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-zinc-400">{t('earned.history')}</span>
            </div>
            <button
              onClick={() => setActiveFilter('history')}
              aria-current={activeFilter === 'history' ? 'page' : undefined}
              className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-zinc-400 ${
                activeFilter === 'history'
                  ? 'rounded-l-none border-l-2 border-cyan-500 bg-zinc-50 font-medium text-zinc-900'
                  : 'rounded-l-none border-l-2 border-transparent text-zinc-500 hover:bg-zinc-100/70 hover:text-zinc-800'
              }`}
            >
              <Clock size={13} className="shrink-0" aria-hidden="true" />
              {t('earned.history')}
            </button>
          </div>
        </nav>

        <div className="min-w-0 flex-1 pl-6">
          <div className="pb-2">
            <p className="mb-1 text-xs text-zinc-400">
              {activeFilter === 'history'
                ? t('earned.history')
                : activeFilter
                ? t(CONTENT_TYPE_I18N_KEY[activeFilter])
                : t('earned.overview')}
            </p>
            <h2 className="text-xl font-semibold tracking-tight text-zinc-950">
              {activeFilter === 'history'
                ? t('earned.historyTitle')
                : activeFilter
                ? `${t(CONTENT_TYPE_I18N_KEY[activeFilter])} ${t('earned.recommendationsSuffix')}`
                : t('earned.allRecommendations')}
            </h2>
          </div>

          <div className="py-1">
            <div className="overflow-x-auto">
              <div className="min-w-[560px]">
                <div className="flex divide-x divide-zinc-200">
                  {[
                    { label: t('earned.allEarnedActions'), value: stats.total },
                    { label: t('earned.doneActions'), value: stats.done },
                    { label: t('earned.skippedActions'), value: stats.declined },
                    { label: t('earned.todoActions'), value: stats.todo },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex-1 px-3 py-1.5">
                      <p className="text-xs text-zinc-500">{label}</p>
                      <p className="mt-0.5 text-xl font-bold tabular-nums text-zinc-950">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="pt-2">
            <div className="overflow-x-auto pb-1">
              <div className="flex min-w-max gap-2">
                {filtered.map((rec) => (
                  <div key={rec.id} className="w-[260px] shrink-0 sm:w-[300px]">
                    <RecommendationCard rec={rec} onStatusChange={updateStatus} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="pt-3">
            <h3 className="text-sm font-semibold text-zinc-950">{t('earned.workspaceTitle')}</h3>
            <p className="mt-0.5 text-xs text-zinc-400">{t('earned.workspaceSubtitle')} ({filtered.length})</p>

            <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-12">
              <div className="xl:col-span-5 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
                <label className="mb-1 block text-xs font-medium text-zinc-700">{t('earned.coreKeyword')}</label>
                <input
                  value={coreKeyword}
                  onChange={(e) => setCoreKeyword(e.target.value)}
                  placeholder={t('earned.coreKeywordPlaceholder')}
                  className="h-9 w-full rounded-lg border border-zinc-200 px-3 text-sm text-zinc-900 outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
                />

                <div className="mt-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs font-medium text-zinc-700">{t('earned.phrases')}</span>
                    <button
                      onClick={generatePhrases}
                      className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 px-2 py-1 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50"
                    >
                      <Sparkles size={12} aria-hidden="true" />
                      {t('earned.generatePhrases')}
                    </button>
                  </div>
                  <div className="flex min-h-16 flex-wrap gap-1.5 rounded-lg border border-zinc-200 bg-zinc-50 p-2">
                    {phrases.length === 0 ? (
                      <span className="text-xs text-zinc-400">-</span>
                    ) : (
                      phrases.map((phrase) => (
                        <span key={phrase} className="rounded-md bg-white px-2 py-1 text-[11px] text-zinc-700 border border-zinc-200">
                          {phrase}
                        </span>
                      ))
                    )}
                  </div>
                </div>

                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-zinc-700">{t('earned.generationCount')}</label>
                    <select
                      value={generationCount}
                      onChange={(e) => setGenerationCount(Number(e.target.value))}
                      className="h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm text-zinc-900 outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
                    >
                      {[3, 5, 10, 20].map((count) => (
                        <option key={count} value={count}>{count}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className="mb-1 flex items-center justify-between">
                      <label className="text-xs font-medium text-zinc-700">{t('earned.materialSelection')}</label>
                      <button
                        onClick={() => onQuickNavigate?.('materials')}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-cyan-700 hover:text-cyan-800"
                      >
                        {t('earned.goMaterials')}
                        <ArrowRight size={11} aria-hidden="true" />
                      </button>
                    </div>
                    <select
                      value={materialSource}
                      onChange={(e) => setMaterialSource(e.target.value as EarnedDraftMaterialSource)}
                      className="h-9 w-full rounded-lg border border-zinc-200 px-2 text-sm text-zinc-900 outline-none focus-visible:ring-2 focus-visible:ring-zinc-400"
                    >
                      <option value="auto">{t('earned.materialAuto')}</option>
                      <option value="brand-library">{t('earned.materialBrandLibrary')}</option>
                      <option value="partner-assets">{t('earned.materialPartnerAssets')}</option>
                    </select>
                  </div>
                </div>

                <button
                  onClick={runGeneration}
                  className="mt-4 inline-flex h-9 items-center gap-1.5 rounded-lg bg-zinc-900 px-3.5 text-xs font-medium text-white hover:bg-zinc-800"
                >
                  <Sparkles size={13} aria-hidden="true" />
                  {t('earned.runGeneration')}
                </button>
              </div>

              <div className="xl:col-span-7">
                <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
                  <h4 className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{t('earned.generatedList')}</h4>
                  <div className="mt-3 space-y-2">
                    {generatedDrafts.map((draft) => (
                      <button
                        key={draft.id}
                        type="button"
                        onClick={() => setSelectedDraft(draft)}
                        className="w-full rounded-lg border border-zinc-200 px-3 py-3 text-left outline-none transition-colors hover:bg-zinc-50 focus-visible:ring-2 focus-visible:ring-zinc-400 focus-visible:ring-offset-1"
                        aria-label={`${t('earned.viewDetails')} ${draft.title}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold text-zinc-900">{draft.title}</p>
                            <p className="mt-0.5 truncate text-xs text-zinc-500">{draft.phrase}</p>
                          </div>
                          <span className={`shrink-0 text-[10px] font-medium ${draft.status === 'running' ? 'text-zinc-600' : 'text-emerald-600'}`}>
                            {draft.status === 'running' ? t('earned.taskRunning') : t('earned.taskDone')}
                          </span>
                        </div>

                        <div className="mt-3 flex items-center justify-between gap-2 border-t border-zinc-100 pt-2">
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-zinc-500">
                            <FileText size={12} aria-hidden="true" />
                            {t('earned.viewDetails')}
                          </span>
                          <ArrowRight size={13} className="text-zinc-400" aria-hidden="true" />
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <EarnedDraftDetailDialog
        draft={selectedDraft}
        isOpen={selectedDraft !== null}
        onClose={() => setSelectedDraft(null)}
      />
    </PageShell>
  );
};

export default EarnedPage;
